import React, { useState } from "react";
import { useGetEmployeesQuery, useCreateEmployeeMutation, useUpdateEmployeeMutation, useDeleteEmployeeMutation } from "../services/api";
import { FiUserCheck, FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";

export default function EmployeesPage() {
  const { data, isLoading } = useGetEmployeesQuery();
  const [createEmployee] = useCreateEmployeeMutation();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [deleteEmployee] = useDeleteEmployeeMutation();

  const employees = data?.data || [];

  // Calculate KPIs
  const totalEmployees = employees.length;
  const activeEmployees = employees.filter(e => e.status === "active").length;
  const inactiveEmployees = employees.filter(e => e.status !== "active").length;
  const adminCount = employees.filter(e => e.role === "admin").length;

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", role: "cashier", status: "active", password: "" });
  const [error, setError] = useState("");

  const openModal = (employee = null) => {
    if (employee) {
      setEditingId(employee.id);
      setForm({ first_name: employee.first_name, last_name: employee.last_name, email: employee.email, phone: employee.phone || "", role: employee.role, status: employee.status, password: "" });
    } else {
      setEditingId(null);
      setForm({ first_name: "", last_name: "", email: "", phone: "", role: "cashier", status: "active", password: "" });
    }
    setError("");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await updateEmployee({ id: editingId, ...form }).unwrap();
      } else {
        await createEmployee(form).unwrap();
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to save employee");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this employee?")) {
      try {
        await deleteEmployee(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || "Failed to delete");
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Employees
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Manage staff and user accounts
          </p>
        </div>
        <button 
          onClick={() => openModal()}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
            color: "white",
            border: "none",
            borderRadius: "8px",
            fontSize: "0.95rem",
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
          }}
        >
          <FiPlus /> Add Employee
        </button>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Employees */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              👤
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Employees
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalEmployees}
          </div>
        </div>

        {/* Active Employees */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ✓
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Active Employees
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {activeEmployees}
          </div>
        </div>

        {/* Inactive Employees */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ⏸️
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Inactive Employees
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {inactiveEmployees}
          </div>
        </div>

        {/* Admins */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              👑
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Administrators
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {adminCount}
          </div>
        </div>
      </div>

      {/* Employees Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          All Employees
        </h3>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Name</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Email</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Phone</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Role</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Status</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                  <FiUserCheck size={48} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
                  <div>No employees yet</div>
                </td>
              </tr>
            ) : (
              employees.map((emp) => (
                <tr key={emp.id} style={{ 
                  borderBottom: "1px solid #F1F5F9",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
                >
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>{emp.first_name} {emp.last_name}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{emp.email}</td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{emp.phone || "-"}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: "#FEF3C7",
                      color: "#92400E"
                    }}>
                      {emp.role}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: emp.status === "active" ? "#D1FAE5" : "#F1F5F9",
                      color: emp.status === "active" ? "#065F46" : "#64748B"
                    }}>
                      {emp.status}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button 
                        onClick={() => openModal(emp)}
                        style={{
                          padding: "0.5rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#3B82F6",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#F8FAFC";
                          e.currentTarget.style.borderColor = "#3B82F6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleDelete(emp.id)}
                        style={{
                          padding: "0.5rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#EF4444",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#FEF2F2";
                          e.currentTarget.style.borderColor = "#EF4444";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Employee Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? "Edit Employee" : "Add Employee"}</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input type="text" className="form-input" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input type="text" className="form-input" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={!!editingId} />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input type="tel" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Role *</label>
                    <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="cashier">Cashier</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status *</label>
                    <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {!editingId && (
                  <div className="form-group">
                    <label className="form-label">Password {!editingId && "*"}</label>
                    <input type="password" className="form-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editingId} placeholder={editingId ? "Leave blank to keep current" : ""} />
                  </div>
                )}

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
